import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import supabase from "../../supabaseClient";
import {
	Button,
	Box,
	Typography,
	Paper,
	Divider,
	List,
	ListItem,
	ListItemText,
	Chip,
	CircularProgress,
} from "@mui/material";

const CheckoutSuccess = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const [order, setOrder] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const orderId = params.get("order_id");
		const token = params.get("token");
		if (!orderId) {
			setError("Missing order ID");
			setLoading(false);
			return;
		}
		const fetchOrder = async () => {
			try {
				const { data, error } = await supabase.functions.invoke(
					"get-order-details",
					{
						body: { order_id: orderId, token },
					}
				);
				if (error || !data || !data.order) {
					setError(data?.error || "Order not found");
				} else {
					setOrder(data.order);
					// Clear cart on success
					localStorage.removeItem("cart");
					localStorage.removeItem("cart_order_id");
				}
			} catch (err) {
				setError("Failed to fetch order");
			}
			setLoading(false);
		};
		fetchOrder();
	}, [location.search]);

	const formatDateOnly = (dateStr) => {
		const [y, m, d] = dateStr.split("-");
		return `${new Date(y, m - 1, d).toLocaleDateString("en-US", {
			year: "numeric",
			month: "long",
			day: "numeric",
		})}`;
	};

	if (loading)
		return (
			<Box sx={{ mt: 8, textAlign: "center" }}>
				<CircularProgress />
			</Box>
		);
	if (error)
		return (
			<Box sx={{ mt: 8, textAlign: "center" }}>
				<Typography color="error" variant="h6">
					{error}
				</Typography>
			</Box>
		);
	if (!order) return null;

	return (
		<Box sx={{ maxWidth: 600, mx: "auto", mt: 8 }}>
			<Paper elevation={3} sx={{ p: { xs: 2, sm: 4 }, borderRadius: 3 }}>
				<Typography
					variant="h4"
					fontWeight={700}
					gutterBottom
					align="center"
					color="primary"
				>
					Thank you for your order!
				</Typography>
				<Typography variant="body1" align="center" sx={{ mb: 2 }}>
					Your order has been received and is being processed.
				</Typography>
				<Divider sx={{ my: 2 }} />
				<Box sx={{ mb: 2 }}>
					<Typography variant="subtitle2">Order ID:</Typography>
					<Typography variant="body2" sx={{ mb: 1 }}>
						{order.id}
					</Typography>
					<Typography variant="subtitle2">Status:</Typography>
					<Chip
						label={order.status.toUpperCase()}
						color={order.status === "paid" ? "success" : "warning"}
						size="small"
						sx={{ mb: 1 }}
					/>
					<Typography variant="subtitle2">Total:</Typography>
					<Typography variant="body2" sx={{ mb: 1 }}>
						${(order.total_cents / 100).toFixed(2)}
					</Typography>
					<Typography variant="subtitle2">Placed:</Typography>
					<Typography variant="body2">
						{new Date(order.created_at).toLocaleString()}
					</Typography>
				</Box>
				{order.order_items && order.order_items.length > 0 && (
					<Box sx={{ mt: 3 }}>
						<Typography variant="h6" fontWeight={600} gutterBottom>
							Items
						</Typography>
						<List>
							{order.order_items.map((item, idx) => {
								const storeItem = item.store_item;
								const season = storeItem?.season;
								let fulfillment = null;
								if (season?.fulfillment_start && season?.fulfillment_end) {
									const startStr = formatDateOnly(season.fulfillment_start);
									const endStr = formatDateOnly(season.fulfillment_end);

									fulfillment =
										startStr === endStr
											? `Estimated pickup: ${startStr}`
											: `Estimated pickup: ${startStr} - ${endStr}`;
								}
								return (
									<ListItem
										key={idx}
										disableGutters
										sx={{ px: 0, alignItems: "flex-start" }}
									>
										<ListItemText
											primary={
												<span>
													<strong>{item.quantity}x</strong>{" "}
													{storeItem?.title || "Unknown Item"}
													{storeItem?.item_type && (
														<Chip
															label={storeItem.item_type}
															size="small"
															sx={{ ml: 1 }}
														/>
													)}
												</span>
											}
											secondary={
												<>
													{storeItem?.price_cents && (
														<span style={{ color: "#666" }}>
															${(storeItem.price_cents / 100).toFixed(2)}
														</span>
													)}
													{fulfillment && (
														<span style={{ color: "#1976d2", marginLeft: 12 }}>
															{fulfillment}
														</span>
													)}
												</>
											}
										/>
									</ListItem>
								);
							})}
						</List>
					</Box>
				)}
				<Divider sx={{ my: 3 }} />
				<Box sx={{ display: "flex", justifyContent: "center" }}>
					<Box sx={{ p: 1 }}>
						<Button
							variant="contained"
							color="primary"
							size="large"
							onClick={() => navigate("/store")}
						>
							Continue Shopping
						</Button>
					</Box>

					<Box sx={{ p: 1 }}>
						<Button
							variant="outlined"
							size="large"
							onClick={() => navigate("/")}
						>
							Back to Home
						</Button>
					</Box>
				</Box>
			</Paper>
		</Box>
	);
};

export default CheckoutSuccess;
